<template>
  <lb-dialog
    :value="value"
    :heading="heading"
    width="800"
    @input="$emit('input', $event)"
  >
    <template v-slot:body>
      <form
        autocomplete="off"
        class="fc-notification-form cycle-automation-dialog-form"
        @submit.prevent
      >
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Name</span>
          <lb-string v-model="form.name" label="" data-testid="cycle-escalation-form__name" />
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">Start date <span class="req">*</span></span>
          <lb-date
            v-model="form.startDate"
            label=""
            label-as-hint
            placeholder="Select date"
            :min="$nova.formatDateISO(new Date())"
            :error="errors.startDate ? [errors.startDate] : []"
            @change="onStartDateChange"
            data-testid="cycle-escalation-form__start-date"
          />
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">End date <span class="req">*</span></span>
          <lb-date
            v-model="form.endDate"
            label=""
            label-as-hint
            placeholder="Select date"
            :min="form.startDate"
            :error="errors.endDate ? [errors.endDate] : []"
            @change="errors.endDate = ''"
            data-testid="cycle-escalation-form__end-date"
          />
        </div>
        <div
          class="fc-notification-form__field fc-notification-form__span-2 fc-notification-form__field--chip-wrap cycle-automation-dialog-form__field--chip-wrap"
        >
          <span class="fc-notification-form__label">Category <span class="req">*</span></span>
          <ChipMultiSelect
            v-model="form.category"
            outlined
            placeholder=""
            :items="categories"
            item-text="name"
            item-value="id"
            :error="errors.category"
            :disabled="saving"
            @input="errors.category = ''"
            data-testid="cycle-escalation-form__category"
            data-testid-prefix="cycle-escalation-form__category"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Recipients <span class="req">*</span></span>
          <lb-string
            v-model="form.emails"
            label=""
            placeholder="Separate multiple emails with commas"
            :error="errors.emails ? [errors.emails] : []"
            @input="onEmailsInput"
            data-testid="cycle-escalation-form__emails"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Email subject <span class="req">*</span></span>
          <lb-string
            v-model="form.subject"
            label=""
            :error="errors.subject ? [errors.subject] : []"
            @input="errors.subject = ''"
            data-testid="cycle-escalation-form__subject"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Email body <span class="req">*</span></span>
          <lb-textarea
            v-model="form.mailBody"
            label=""
            :error="errors.mailBody ? [errors.mailBody] : []"
            counter-suffix="characters"
            @input="errors.mailBody = ''"
            data-testid="cycle-escalation-form__body"
          />
        </div>
        <div
          class="fc-notification-form__field fc-notification-form__span-2 fc-notification-form__field--chip-wrap cycle-automation-dialog-form__field--chip-wrap"
        >
          <span class="fc-notification-form__label">Locations <span class="req">*</span></span>
          <ChipMultiSelect
            v-model="form.locations"
            outlined
            placeholder=""
            :items="locationItems"
            item-text="name"
            item-value="_id"
            :error="errors.locations"
            :disabled="saving"
            @input="errors.locations = ''"
            data-testid="cycle-escalation-form__locations"
            data-testid-prefix="cycle-escalation-form__locations"
          />
        </div>
        <div class="fc-notification-form__field">
          <ScheduledAtField
            v-model="form.time"
            label="Sends at"
            compact-timezone
            :error="errors.time"
            data-testid="cycle-escalation-form__hour"
            @input="onTimeChange"
          />
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">SLA <span class="req">*</span></span>
          <lb-dropdown
            v-model="form.sla"
            label=""
            :items="slaList"
            itemtext="name"
            itemvalue="id"
            :error="errors.sla ? [errors.sla] : []"
            @change="errors.sla = ''"
            data-testid="cycle-escalation-form__sla"
          />
          <p class="fc-notification-form__hint">Each option is how long after the due date the email is sent.</p>
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">Priority</span>
          <lb-dropdown
            v-model="form.priority"
            label=""
            :items="priorityList"
            itemtext="name"
            itemvalue="id"
            :disabled="true"
            data-testid="cycle-escalation-form__priority"
          />
        </div>
      </form>
    </template>
    <template #actions>
      <span class="fc-notification-notice">{{ scheduleNotice }}</span>
      <v-spacer />
      <v-btn
        color="primary"
        class="text-none"
        :loading="saving"
        data-testid="cycle-escalation-form__save"
        @click="save"
      >
        {{ form._id ? 'Update' : 'Create' }}
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ChipMultiSelect from '../../../../../components/ChipMultiSelect/ChipMultiSelect.vue';
import { ScheduledAtField } from '../../../../../components/Notifications';
import { CRON, EMAIL_NOTIFICATION, ESCALATION_SLA_OPTIONS, SCHEDULE_CHANGE_NOTICE } from '../../../../../utils/constants';
import {
  localStartTimeFromSchedule,
  localTimeFromLegacyHour,
  utcHourTokenFromLocalTime,
} from '../../../../../utils/notificationScheduleUtils';
import { formatEmailList, parseEmailList, validateEmailListField } from '../../../../../utils/validationUtils';

const EMPTY_ERRORS = () => ({
  startDate: '',
  endDate: '',
  emails: '',
  subject: '',
  mailBody: '',
  locations: '',
  time: '',
  sla: '',
  category: '',
});

const EMPTY_FORM = () => ({
  name: '',
  startDate: '',
  endDate: '',
  category: [],
  emails: '',
  subject: '',
  mailBody: '',
  locations: [],
  time: '',
  sla: '',
  priority: 'High',
});

export default {
  name: 'EscalationFormDialog',
  components: { ChipMultiSelect, ScheduledAtField },
  props: {
    value: { type: Boolean, default: false },
    notification: { type: Object, default: () => ({}) },
    cycleId: { type: String, default: '' },
  },
  data() {
    return {
      saving: false,
      form: EMPTY_FORM(),
      errors: EMPTY_ERRORS(),
      slaList: ESCALATION_SLA_OPTIONS,
      priorityList: [{ name: 'High', id: 'High' }],
      scheduleNotice: SCHEDULE_CHANGE_NOTICE,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', { categories: 'fetchCategories', setupLocations: 'fetchLocations' }),
    ...mapGetters('dashboard', ['getActiveLocations']),
    heading() {
      return this.form._id ? 'Edit escalation' : 'Add escalation';
    },
    locationItems() {
      const setup = this.setupLocations || [];
      const cycle = this.getActiveLocations || [];
      const raw = setup.length ? setup : cycle;
      return raw.map((loc) => ({
        ...loc,
        _id: loc._id || loc.id,
        name: loc.name,
      }));
    },
  },
  watch: {
    value(open) {
      if (!open) return;
      this.$store.dispatch('emailNotifications/fetchLocations');
      this.$store.dispatch('emailNotifications/fetchCategories');
      if (this.cycleId) this.getCountryWiseLocations({ cycleId: this.cycleId });
      this.hydrate(this.notification);
    },
  },
  methods: {
    ...mapActions('dashboard', ['getCountryWiseLocations']),
    ...mapActions('tasks', ['addTaskEmailNotification', 'updateTaskEmailNotification']),
    hydrate(item) {
      this.errors = EMPTY_ERRORS();
      if (!item || !item._id) {
        this.form = EMPTY_FORM();
        return;
      }
      const ensureArray = (value) => {
        if (Array.isArray(value)) return [...value];
        if (value === undefined || value === null || value === '') return [];
        return [value];
      };
      const schedulerTiming = item.schedulerTiming ? String(item.schedulerTiming) : '';
      this.form = {
        ...EMPTY_FORM(),
        _id: item._id,
        name: item.name || '',
        subject: item.subject || '',
        mailBody: item.mailBody || '',
        endDate: item.endDate || item.meta?.endDate || item.end || item.meta?.end || '',
        startDate: item.startDate || item.meta?.startDate || item.start || item.meta?.start || '',
        locations: ensureArray(item.meta?.location_key).map(String),
        emails: formatEmailList(item.meta?.toUsers),
        time: (item.meta?.schedule?.startTime
          ? localStartTimeFromSchedule(item.meta.schedule)
          : localTimeFromLegacyHour(CRON.cronFormatToHour(schedulerTiming))) || '',
        sla: item.sla ?? item.meta?.sla ?? '',
        category: ensureArray(item.meta?.category),
        priority: item.priority || item.meta?.priority || 'High',
      };
    },
    onEmailsInput(value) {
      const { error } = validateEmailListField(value !== undefined ? value : this.form.emails, {
        requiredMessage: '',
        invalidMessage: '',
        duplicateMessage: EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL,
      });
      this.errors.emails = error || '';
    },
    onStartDateChange() {
      this.errors.startDate = '';
    },
    onTimeChange() {
      this.errors.time = '';
    },
    validate() {
      this.errors = EMPTY_ERRORS();
      let isValid = true;
      const details = this.form;
      const errors = this.errors;
      if (!details.startDate) {
        errors.startDate = 'Start Date is required';
        isValid = false;
      }
      if (!details.endDate) {
        errors.endDate = 'End Date is required';
        isValid = false;
      } else if (details.startDate && new Date(details.endDate) < new Date(details.startDate)) {
        errors.endDate = 'End Date must be after Start Date';
        isValid = false;
      }
      if (!details.category || details.category.length === 0) {
        errors.category = 'At least one category is required';
        isValid = false;
      }
      const emailValidation = validateEmailListField(details.emails, {
        duplicateMessage: EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL,
      });
      if (emailValidation.error) {
        errors.emails = emailValidation.error;
        isValid = false;
      }
      if (!details.subject || details.subject.trim() === '') {
        errors.subject = 'Email Subject is required';
        isValid = false;
      }
      if (!details.mailBody || details.mailBody.trim() === '') {
        errors.mailBody = 'Email Body is required';
        isValid = false;
      }
      if (details.time === null || details.time === undefined || details.time === '') {
        errors.time = 'Time is required';
        isValid = false;
      }
      if (details.sla === null || details.sla === undefined || details.sla === '') {
        errors.sla = 'SLA is required';
        isValid = false;
      }
      if (!details.locations || details.locations.length === 0) {
        errors.locations = 'At least one location is required';
        isValid = false;
      }
      if (!isValid) {
        this.$store.commit('sbError', EMAIL_NOTIFICATION.MESSAGES.REQUIRED_VALIDATION);
      }
      return isValid;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const notificationData = {
          _id: this.form._id,
          name: this.form.name,
          subject: this.form.subject,
          mailBody: this.form.mailBody,
          time: utcHourTokenFromLocalTime(this.form.time),
          startTime: this.form.time,
          locations: this.form.locations,
          emails: parseEmailList(this.form.emails),
          startDate: this.form.startDate,
          endDate: this.form.endDate,
          sla: this.form.sla,
          category: this.form.category,
          priority: this.form.priority,
        };
        const cycleId = this.cycleId;
        const result = notificationData._id
          ? await this.updateTaskEmailNotification({ cycleId, notificationData })
          : await this.addTaskEmailNotification({ cycleId, notificationData });
        if (result && result.success === false) return;
        this.$emit('saved');
        this.$emit('input', false);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../../../../components/Notifications/notificationLayout.scss';
@import '../../../../../components/Notifications/cycleEscalationDialogForm.scss';
</style>
